import { z } from 'zod';
import {
  AuditSchema,
  BusinessIdSchema,
  ClockTimeSchema,
  CurrencyCodeSchema,
  HandlingClassSchema,
  UnitSchema,
  UserIdSchema,
  WeekdaySchema,
} from './primitives.js';

/* ------------------------------------------------------------------ *
 * Locale
 *
 * Three shipped locales. `es` runs ~25% longer than `en`, which is a
 * layout constraint, not a translation one — see the `--label-*` tokens
 * in the web app. `pseudo` is a build-time locale that inflates every
 * string by 40% and brackets it; it is how we catch overflow before a
 * Spanish-speaking customer does.
 * ------------------------------------------------------------------ */

export const LocaleSchema = z.enum(['en', 'es', 'ru']);
export type Locale = z.infer<typeof LocaleSchema>;

export const SUPPORTED_LOCALES: readonly Locale[] = ['en', 'es', 'ru'] as const;
export const DEFAULT_LOCALE: Locale = 'en';

/* ------------------------------------------------------------------ *
 * Tax
 *
 * Not "VAT 21%". A scheme, because the platform has to hold Spanish
 * IVA with recargo de equivalencia next to a flat-rate tenant next to
 * one that is out of scope entirely. Rates are per-scheme so a product
 * carries a *rate key*, never a number.
 * ------------------------------------------------------------------ */

export const TaxRateSchema = z.object({
  /** Stable key referenced by products: 'standard' | 'reduced' | 'super-reduced' | 'zero' | ... */
  key: z.string().min(1),
  label: z.string().min(1),
  /** Basis points: 2100 = 21%. Integers keep rounding honest. */
  bps: z.number().int().min(0).max(10_000),
  /** Spanish recargo de equivalencia, added on top for retail resellers. */
  surchargeBps: z.number().int().min(0).max(10_000).default(0),
});
export type TaxRate = z.infer<typeof TaxRateSchema>;

export const TaxSchemeSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  /** ISO 3166-1 alpha-2. Drives the invoice legal template. */
  country: z.string().length(2),
  /** Are catalog prices shown to buyers gross or net? Changes every price cell in the app. */
  pricesIncludeTax: z.boolean().default(false),
  rates: z.array(TaxRateSchema).min(1),
  defaultRateKey: z.string().min(1),
  /** Line-level vs document-level rounding. Tax authorities disagree; tenants must choose. */
  rounding: z.enum(['per-line', 'per-document']).default('per-line'),
});
export type TaxScheme = z.infer<typeof TaxSchemeSchema>;

/* ------------------------------------------------------------------ *
 * Business (the tenant)
 * ------------------------------------------------------------------ */

export const BusinessKindSchema = z.enum([
  'wholesaler',   // buys at market, sells to venues — the anchor tenant
  'producer',     // farm, sells direct
  'venue',        // restaurant / bar / hotel, buys
  'distributor',  // logistics-first
  'other',
]);
export type BusinessKind = z.infer<typeof BusinessKindSchema>;

export const BusinessSettingsSchema = z.object({
  currency: CurrencyCodeSchema,
  /** IANA zone. Every instant in the UI is rendered through this, never through the browser's. */
  timezone: z.string().min(1),
  /** Formatting locale, independent of UI language: a Russian-speaking user in Spain wants 1.234,56 €. */
  formatLocale: z.string().min(2),
  defaultLocale: LocaleSchema,
  enabledLocales: z.array(LocaleSchema).min(1),
  taxScheme: TaxSchemeSchema,
  /** First day of week for every calendar and week picker. */
  weekStart: WeekdaySchema.default('mon'),
  /** When the trading day flips. A market wholesaler's "today" starts at 03:00, not midnight. */
  dayStartsAt: ClockTimeSchema.default('00:00'),
  /** Cutoff after which an order rolls to the next delivery day. */
  orderCutoff: ClockTimeSchema.default('18:00'),
  /** Units offered in pickers, narrowed from the full set so a fishmonger never sees 'hl'. */
  unitsInUse: z.array(UnitSchema).min(1),
  /** Which handling classes this tenant's trait pack exposes. Drives the colour bands. */
  handlingClasses: z.array(HandlingClassSchema).min(1),
  /** Rounding for weight-priced lines, in grams. Scales at Mercabarna read to 10 g. */
  weightPrecisionG: z.number().int().positive().default(10),
});
export type BusinessSettings = z.infer<typeof BusinessSettingsSchema>;

/**
 * Feature modules are enabled per business. The web app's module
 * registry reads this and composes navigation, routes and search from
 * it — which is why adding a module later is one file, not a refactor.
 */
export const ModuleIdSchema = z.enum([
  'dashboard',
  'orders',
  'grouping',      // day demand per product, supplier assignment
  'market',        // morning price board + buying
  'purchasing',    // shopping lists, buyer runs
  'suppliers',     // per-supplier sheet: what to bring today
  'logistics',
  'deliveries',    // the day's delivery list — la lista de entregas
  'catalog',
  'products',      // product manager (write side of catalog)
  'stock',
  'contacts',
  'messaging',
  'schedule',
  'invoices',
  'payments',
  'analytics',
  'team',
  'settings',
]);
export type ModuleId = z.infer<typeof ModuleIdSchema>;

export const BusinessSchema = AuditSchema.extend({
  id: BusinessIdSchema,
  /** URL segment: /b/:slug/orders */
  slug: z.string().regex(/^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/),
  name: z.string().min(1),
  legalName: z.string().min(1),
  taxId: z.string().nullable().default(null),
  kind: BusinessKindSchema,
  logoUrl: z.string().url().nullable().default(null),
  settings: BusinessSettingsSchema,
  enabledModules: z.array(ModuleIdSchema),
  /** Trait pack id: 'fresh-food' ships by default; the model is not tied to it. */
  traitPack: z.string().default('fresh-food'),
  status: z.enum(['active', 'trial', 'suspended']).default('trial'),
});
export type Business = z.infer<typeof BusinessSchema>;

/* ------------------------------------------------------------------ *
 * Users and membership
 *
 * A user is a person on the platform. A membership is that person
 * inside one business, with one role. The same person can be a buyer
 * in one tenant and an owner in another — which is exactly what
 * happens when a wholesaler and their farm both run on Prody.
 * ------------------------------------------------------------------ */

export const UserSchema = AuditSchema.extend({
  id: UserIdSchema,
  email: z.string().email(),
  name: z.string().min(1),
  phone: z.string().nullable().default(null),
  avatarUrl: z.string().url().nullable().default(null),
  /** UI language preference; falls back to the business default. */
  locale: LocaleSchema.nullable().default(null),
  lastSeenAt: z.string().datetime({ offset: true }).nullable().default(null),
});
export type User = z.infer<typeof UserSchema>;

export const RoleSchema = z.enum([
  'owner',      // billing, tenant settings, can delete the business
  'manager',    // everything operational
  'sales',      // orders, customers, personal prices
  'buyer',      // market, purchasing, supplier prices
  'driver',     // logistics only, mobile-first
  'warehouse',  // stock, picking, shortfalls
  'accountant', // invoices, payments, debt
  'viewer',     // read-only
]);
export type Role = z.infer<typeof RoleSchema>;

export const MembershipSchema = AuditSchema.extend({
  businessId: BusinessIdSchema,
  userId: UserIdSchema,
  role: RoleSchema,
  /** Extra grants on top of the role, for the inevitable "driver who also invoices". */
  extraPermissions: z.array(z.string()).default([]),
  status: z.enum(['invited', 'active', 'disabled']).default('invited'),
});
export type Membership = z.infer<typeof MembershipSchema>;

/** What the app loads once at boot: who I am, and where I can go. */
export const SessionSchema = z.object({
  user: UserSchema,
  memberships: z.array(
    z.object({
      business: BusinessSchema,
      role: RoleSchema,
      extraPermissions: z.array(z.string()),
    }),
  ),
});
export type Session = z.infer<typeof SessionSchema>;
